<svelte:options customElement="rf-inspector" />

<script lang="ts">
	/**
	 * Imports
	 */
	import { collections } from '@store/collections.svelte.ts';
	import ReframeInspectorCard from './rf-inspector-card.svelte';
	import inspectorStore from '@store/inspector.svelte';
	import { onDestroy, onMount } from 'svelte';
	import ReframeInspectorControls from './rf-inspector-controls.svelte';
	import type { RFCanvas, RFCanvasMeta } from '@src/types/reframe';
	import ReframeInspectorSidepanel from './rf-inspector-sidepanel.svelte';
	import cssVariables from '@src/utils/cssvariables';
	import ReframeCollectionCard from '@src/core/rf-collection-card/rf-collection-card.svelte';
	import { BREAKPOINTS } from '@src/utils/breakpoints';
	import debounced from '@src/utils/debounced';
	import { onEvent } from '@src/utils/events';
	/**
	 * Props
	 */
	let {
		draggingMessage = 'Drop a canvas here',
		emptyMessage = 'No canvases selected, add or drag and drop a canvas here',
		metadataTitle = 'Metadata',
		collectionTitle = 'Collections',
	} = $props();

	/**
	 * Constants
	 */
	const DEFAULT_GRID_COLUMNS = 1;
	const MAX_GRID_COLUMNS = 5;

	/**
	 * States
	 */
	let isExpanded = $state(false);
	let isFullscreen = $state(false);
	let canvasPanel: RFCanvas | null = $state(null);
	let panelsData = $state({
		left: { width: 0, height: 0, open: true },
		right: { width: 0, height: 0, open: true },
	});
	let gridMode: 'horizontal' | 'vertical' | 'masonry' = $state('horizontal');
	let gridColumns = $state(DEFAULT_GRID_COLUMNS);
	let dragging = $state(false);
	let canvasKey = $state(0);
	let inspectorElement: HTMLDivElement | null = $state(null);
	let viewerElement: HTMLDivElement | null = $state(null);
	let hasMetadata = $state(false);
	let hideMetadataPanel = $state(false);
	let viewerOffset = $state(0);
	let isMobile = $state(window.innerWidth <= BREAKPOINTS.s);
	let isResizing = $state(false);
	let resizeFrame: number | null = $state(null);
	let hasManualColumns = $state(false);

	/**
	 * Derived values for responsive layout
	 */
	const selectedCanvases = $derived(inspectorStore.selectedCanvases);
	const mode = $derived(inspectorStore.inspectorMode);
	const isVisible = $derived(inspectorStore.isVisible);
	const selectedCollection = $derived(collections.find((collection) => collection.uuid === selectedCanvases[0]?.collectionId));

	/**
	 * Methods
	 */
	const handleClose = () => {
		inspectorStore.closeInspector();
		isExpanded = false;
		isFullscreen = false;
		panelsData.right.open = true;
		panelsData.left.open = true;
	};

	const onKeydown = (e: KeyboardEvent) => {
		if (e.key === 'Escape') handleClose();
	};

	const handlePanelResize = () => {
		if (isResizing) return;
		isResizing = true;

		// cancel any pending resize
		if (resizeFrame) {
			cancelAnimationFrame(resizeFrame);
		}

		// add resize frame to handle canvas refresh and viewer offset calculation
		resizeFrame = requestAnimationFrame(() => {
			// Force canvas refresh on resize
			if (canvasPanel && mode === 'canvas') {
				canvasKey++;
			}

			// Calculate viewer offset based on mobile state
			viewerOffset = isMobile ? panelsData.left.height : panelsData.left.width + panelsData.right.width;

			// Handle mobile layout
			if (isMobile) {
				// set the right panel width to 0 and height to 0 and close it
				panelsData.right.width = 0;
				panelsData.right.height = 0;
				panelsData.right.open = false;
				hideMetadataPanel = true;
			} else if (hideMetadataPanel && mode === 'canvas') {
				// if the metadata panel is hidden and the mode is canvas, open the metadata panel
				panelsData.right.open = true;
				hideMetadataPanel = false;
			}

			isResizing = false;
			resizeFrame = null;
		});
	};

	const onResize = (sidepanel: 'left' | 'right', width: number, height: number) => {
		panelsData[sidepanel].width = width;
		panelsData[sidepanel].height = height;
		handlePanelResize();
	};

	const addCanvasToInspector = (canvas: RFCanvasMeta & { key: string }) => {
		if (mode === 'canvas') {
			inspectorStore.clearSelectedCanvases();
			canvasKey++;
		}
		inspectorStore.addSelectedCanvas(canvas);

		// Update grid columns when adding new items in grid mode
		if (mode === 'grid' && !hasManualColumns) {
			const newNumCanvases = selectedCanvases.length;
			gridColumns = Math.min(newNumCanvases, MAX_GRID_COLUMNS);
		}
	};

	const onDrop = (e: DragEvent) => {
		// check if the data exists and if contains canvasId or manifestId
		const data = e?.dataTransfer?.getData('text/plain');
		const isCanvas = data?.includes('canvasId') || data?.includes('manifestId');
		if (!data || !isCanvas) return;
		// parse the data
		const item = JSON.parse(data);
		// set dragging to false
		dragging = false;
		// add the selected canvas to the inspector
		addCanvasToInspector(item);
	};

	const onModeChange = (newMode: 'canvas' | 'grid' | 'light-table') => {
		// clear the selected canvases
		inspectorStore.clearSelectedCanvases();
		// set the inspector mode
		inspectorStore.inspectorMode = newMode;
		// if the mode is grid or light-table, set the right panel width to 0 and handle the panel resize
		if (newMode === 'grid' || newMode === 'light-table') {
			panelsData.right.width = 0;
			handlePanelResize();
		}
		// Reset manual columns flag when changing modes
		hasManualColumns = false;
		gridColumns = DEFAULT_GRID_COLUMNS;
	};

	const handleFullscreen = () => {
		if (inspectorElement && !isFullscreen) {
			inspectorElement.requestFullscreen();
			isFullscreen = true;
		} else if (inspectorElement && isFullscreen) {
			document.exitFullscreen();
			isFullscreen = false;
		}
	};

	const onControlClick = (_: MouseEvent, canvas: RFCanvasMeta & { key: string }) => {
		// if the mode is canvas, clear the selected canvases and increment the canvas key
		addCanvasToInspector(canvas);
	};

	const onDeleteAll = () => {
		inspectorStore.clearSelectedCanvases();
		if (mode === 'canvas') {
			panelsData.right.open = false;
			panelsData.right.width = 0;
		}
	};

	const handleWindowResize = debounced(() => {
		const newIsMobile = window.innerWidth <= BREAKPOINTS.s;
		if (newIsMobile !== isMobile) {
			isMobile = newIsMobile;
			handlePanelResize();
		}
	}, 100);

	/**
	 * Effects
	 */

	$effect(() => {
		// if the inspector is visible, set the body overflow to hidden
		if (inspectorStore.isVisible) {
			document.body.style.setProperty('overflow', 'hidden');
		} else {
			document.body.style.removeProperty('overflow');
		}
	});

	$effect(() => {
		// check if the canvas has metadata
		if (canvasPanel && selectedCanvases[0]?.manifestId) {
			const manifest = IIIF_VAULT?.get(selectedCanvases[0].manifestId);
			hasMetadata = manifest?.metadata?.length > 0;
		}
	});

	$effect(() => {
		// automatically scroll to the bottom of the grid when it's resized
		if (mode === 'grid' && viewerElement) {
			const scrollElement = viewerElement?.querySelector('.rf-inspector__viewer-content');
			onEvent('rf-page-resize', () => {
				scrollElement?.scrollTo({
					top: scrollElement?.scrollHeight,
				});
			});
		}
	});

	/**
	 * Lifecycle
	 */
	onMount(() => {
		// enable inspector
		inspectorStore.enableInspector();
	});

	onDestroy(() => {
		if (viewerElement) {
			viewerElement = null;
		}
		if (resizeFrame) {
			cancelAnimationFrame(resizeFrame);
		}
	});
</script>

<svelte:window onkeydown={onKeydown} onresize={handleWindowResize} />
{#if isVisible}
	<div
		bind:this={inspectorElement}
		id="rf-inspector"
		class={[
			'rf-inspector',
			{
				'rf-inspector--expanded': isExpanded,
				'rf-inspector--dragging': dragging,
				'rf-inspector--empty': selectedCanvases.length === 0,
			},
		]}
		style={cssVariables({
			'--rf-inspector-dragging-message': `"${draggingMessage}"`,
			'--rf-inspector-empty-message': `"${emptyMessage}"`,
		})}
		part="rf-inspector"
		role="dialog"
		aria-modal="true"
		aria-labelledby="rf-inspector-title"
		aria-describedby="rf-inspector-description"
		onscroll={(e) => e.preventDefault()}
	>
		<ReframeInspectorSidepanel
			classNames="rf-inspector__tree"
			title={collectionTitle}
			fullscreen={isFullscreen}
			onresize={(w: number, h: number) => onResize('left', w, h)}
			open={panelsData.left.open}
			onclosepanel={() => (panelsData.left.open = false)}
		>
			{#each collections as collection}
				<ReframeInspectorCard
					item={collection}
					oncontrolclick={onControlClick}
					{mode}
					dragstart={() => (dragging = true)}
					dragend={() => (dragging = false)}
				/>
			{/each}
		</ReframeInspectorSidepanel>

		<div
			class="rf-inspector__viewer"
			style={cssVariables({ '--viewer-offset': viewerOffset + 'px' })}
			role="presentation"
			ondrop={onDrop}
			ondragover={(e) => e.preventDefault()}
			bind:this={viewerElement}
		>
			<div class="rf-inspector__viewer-content">
				{#if selectedCanvases.length > 0 && mode === 'canvas'}
					{#key canvasKey}
						<rf-canvas
							bind:this={canvasPanel}
							canvas-id={selectedCanvases[0].canvasId}
							manifest-id={selectedCanvases[0].manifestId}
							src={selectedCanvases[0].src}
							interactive="true"
							fit="contain"
							legend="false"
							in-inspector="true"
							inspector-key={selectedCanvases[0].key}
						></rf-canvas>
					{/key}
				{/if}
				{#if selectedCanvases.length > 0 && mode === 'grid'}
					<rf-grid layout={gridMode} columns={gridColumns} responsive={false} debug={true}>
						{#each selectedCanvases as canvas (canvas.key)}
							<rf-canvas
								src={canvas.src}
								manifest-id={canvas.manifestId}
								canvas-id={canvas.canvasId}
								legend="false"
								in-inspector="true"
								inspector-key={canvas.key}
							></rf-canvas>
						{/each}
					</rf-grid>
				{/if}
				{#if mode === 'light-table'}
					<rf-light-table in-inspector={true} debug={true} responsive={false}>
						{#each selectedCanvases as canvas (canvas.key)}
							<rf-light-table-element>
								<rf-canvas
									src={canvas.src}
									manifest-id={canvas.manifestId}
									canvas-id={canvas.canvasId}
									legend="false"
									in-inspector="true"
									inspector-key={canvas.key}
								></rf-canvas>
							</rf-light-table-element>
						{/each}
					</rf-light-table>
				{/if}
			</div>
			<ReframeInspectorControls
				onexpand={() => (isExpanded = !isExpanded)}
				onzoomout={() => canvasPanel?.zoomOut()}
				onzoomin={() => canvasPanel?.zoomIn()}
				ondeleteall={onDeleteAll}
				onfullscreen={handleFullscreen}
				onclose={handleClose}
				onmodechange={onModeChange}
				ongridmodechange={(mode) => (gridMode = mode)}
				oncolumnschange={(columns) => {
					gridColumns = columns;
					hasManualColumns = true;
				}}
				hasCanvases={selectedCanvases.length > 0}
				{gridMode}
				{gridColumns}
				{isExpanded}
				{isFullscreen}
				{isMobile}
				{mode}
			/>
		</div>

		{#if mode === 'canvas' && selectedCanvases.length > 0 && !hideMetadataPanel}
			<ReframeInspectorSidepanel
				classNames="rf-inspector__meta"
				title={metadataTitle}
				fullscreen={isFullscreen}
				right={true}
				onresize={(w: number, h: number) => onResize('right', w, h)}
				open={panelsData.right.open}
				onclosepanel={() => (panelsData.right.open = false)}
			>
				{#if selectedCollection}
					<div class="rf-inspector__meta__collection">
						<h4 class="rf-inspector__meta__title">Collection metadata</h4>
						<ReframeCollectionCard item={selectedCollection} inspector={true} />
					</div>
				{/if}
				{#key canvasKey}
					{#if hasMetadata}
						<div class="rf-inspector__meta__manifest">
							<h4 class="rf-inspector__meta__title">IIIF manifest metadata</h4>
							<metadata-panel
								manifest-id={selectedCanvases[0]?.manifestId}
								variation="list"
								label-style="bold"
								label-width="250"
								allow-html="true"
								bordered="true"
								separator="<br/>"
							></metadata-panel>
						</div>
					{/if}
				{/key}
			</ReframeInspectorSidepanel>
		{/if}
	</div>
{/if}

<style lang="scss" global>
	@use 'assets/scss/core/rf-breakpoints' as breakpoint;

	.rf-inspector {
		position: fixed;
		top: 0;
		left: 0;

		z-index: var(--z-index-inspector, 9999);

		display: flex;
		justify-content: space-between;
		align-items: flex-start;

		width: 100vw;
		height: 100dvh;

		background-color: rgb(23, 23, 23);
		backdrop-filter: blur(8px);

		@media screen and (max-width: breakpoint.$s) {
			flex-direction: column;
			justify-content: flex-start;
		}

		&__viewer {
			width: calc(100% - var(--viewer-offset));
			height: 100%;
			position: relative;

			--rf-canvas-height: 100%;
			--rf-primary-color: rgb(23, 23, 23);

			@media screen and (max-width: breakpoint.$s) {
				width: 100%;
				height: calc(100dvh - var(--viewer-offset));
			}

			.rf-inspector--expanded & {
				width: 100%;
			}

			&::before,
			&::after {
				content: '';
				position: absolute;
				inset: 0;
				opacity: 0;
				display: flex;
				justify-content: center;
				align-items: center;
				pointer-events: none;
				z-index: 12;
				color: white;
				font-family: var(--rf-font-family);
				font-size: 16px;
				text-align: center;
			}

			&::after {
				background-color: rgba(0, 0, 0, 0.5);
				transition:
					opacity 0.3s ease-in-out,
					backdrop-filter 0.3s ease-in-out;
				pointer-events: none;
			}
			&::before {
				content: '';
				opacity: 0;
				inset: var(--rf-spacing-xs, 4px);
				border: 2px dashed rgba(255, 255, 255, 0.5);
				border-radius: 4px;
				z-index: 13;
				transition: opacity 0.3s ease-in-out;
			}

			.rf-inspector--dragging & {
				&::after {
					content: '' var(--rf-inspector-dragging-message) '';
					opacity: 1;
					backdrop-filter: blur(8px);
				}
				&::before {
					opacity: 1;
				}
			}

			.rf-inspector--empty:not(.rf-inspector--dragging) & {
				&::after {
					content: '' var(--rf-inspector-empty-message) '';
					opacity: 1;
					backdrop-filter: blur(8px);
				}
			}
		}

		&__viewer-content {
			width: 100%;
			height: 100%;
			overflow: auto;
			position: relative;
			overflow-x: hidden;
		}

		&__tree {
			top: 0px;
			left: 8px;
			margin-top: 8px;
			--grid-template: repeat(1, 1fr);

			.rf-inspector--expanded & {
				position: absolute;
			}
		}

		&__meta {
			top: 0px;
			right: 8px;
			margin-top: 8px;

			.rf-inspector--expanded & {
				position: absolute;
			}

			&__title {
				margin: 0;
				margin-bottom: var(--rf-spacing-s, 8px);
				color: var(--rf-inspector-control-color, #fff);
				font-family: var(--rf-headings-font);
			}

			tbody {
				display: flex;
				flex-direction: column;
				gap: 8px;
			}

			.metadata-row {
				display: flex;
				flex-flow: column;
				color: white;
				font-family: var(--rf-font-family);
				background-color: var(--rf-inspector-card-bg-color, rgba(50, 50, 50, 0.5));
				padding: 8px;
				border-radius: 4px;

				td:first-child {
					padding-bottom: var(--rf-spacing-xs, 4px);
					border-bottom: solid var(--rf-inspector-control-border-color, rgba(150, 150, 150, 0.1));
				}

				td {
					p {
						margin: 0;
						margin-bottom: 1rem;

						&:last-child {
							margin: 0;
						}
					}
				}
			}
		}
	}
</style>
